import React from 'react';
import styles from './product.module.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useDuplicateProductMutation,
  useGetProductAddOnsQuery,
  useGetProductQuery,
  useUpdateProductMutation,
} from './products.api';
import { FormGroup } from 'shared-components';
import { FieldArray, Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import Routes from '../../app/routes';
import { Currency } from '../currency/currency';
import { Button } from 'shared-components';
import { useAppDispatch } from '../../app/hooks';
import { open } from '../modal/modalSlice';
import {
  DeliveryMechanism,
  IProduct,
  PricingModel,
  ProductScope,
} from '@topshots/core-models';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Label } from 'shared-components';
import { Card, CardContent } from 'shared-components';
import { FormLayout } from 'shared-components';
import { Input } from 'shared-components';
import { TextArea } from 'shared-components';
import { Checkbox } from 'shared-components';
import { Select } from 'shared-components';
import { PageTitle } from 'shared-components';
import { FormBuilder } from './form-builder';
import { IActionBarFurtherAction } from 'shared-components';
import { ProductPageContent } from './product-page-content';

export const Product = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetProductQuery(id as string);
  const { data: addOnsData, isLoading: addOnsLoading } =
    useGetProductAddOnsQuery(id as string);

  const [update] = useUpdateProductMutation();
  const [duplicate] = useDuplicateProductMutation();

  if (isLoading || !data) {
    return <span>loading...</span>;
  }

  const furtherActions: IActionBarFurtherAction[] = [];
  if (!data?.isConfirmed) {
    furtherActions.push({ value: 'confirm', display: 'Confirm' });
  } else {
    furtherActions.push({ value: 'duplicate', display: 'Duplicate' });
  }
  if (data?.isConfirmed && !data.isArchived) {
    furtherActions.push({ value: 'archive', display: 'Archive' });
  }
  if (data?.isConfirmed && !data?.isArchived && !data?.addOnFor) {
    furtherActions.push({ value: 'createAddOn', display: 'Create AddOn' });
  }
  return (
    <Formik
      initialValues={data}
      enableReinitialize={true}
      validationSchema={yup.object({
        scope: yup.mixed<ProductScope>().oneOf(Object.values(ProductScope)),
        name: yup.string().required(),
        description: yup.string(),
        pricingModel: yup
          .mixed<PricingModel>()
          .oneOf(Object.values(PricingModel)),
        price: yup.number().required(),
        shippingCost: yup.number().required(),
        deliveryMechanism: yup
          .mixed<DeliveryMechanism>()
          .oneOf(Object.values(DeliveryMechanism)),
        imagesRequired: yup.number().required(),
        isInstantDownload: yup.boolean().required(),
        lengthLongSide: yup.number().required(),
      })}
      onSubmit={async (values) => {
        await update({
          id: id as string,
          data: values,
        });
        toast.success('Product updated!', {
          autoClose: 2000,
        });
      }}
    >
      {({ submitForm, setFieldValue, handleSubmit, getFieldProps, values }) => (
        <>
          <PageTitle
            title="Product"
            allowSave={!data?.isArchived}
            crumbs={[
              { display: Routes.DASHBOARD.title, link: Routes.DASHBOARD.link },
              { display: Routes.PRODUCTS.title, link: Routes.PRODUCTS.link },
              {
                display: data?.name ?? '',
                link: Routes.PRODUCT.createLink?.(id as string) ?? '',
              },
            ]}
            onAction={async (action) => {
              switch (action) {
                case 'createAddOn':
                  dispatch(
                    open({
                      title: 'Create AddOn Product',
                      contentKey: 'create-addon',
                      contentPayload: { productId: id },
                    })
                  );
                  break;
                case 'save':
                  submitForm();
                  break;
                case 'duplicate':
                  if (
                    window.confirm(
                      'Are you sure you want to duplicate this product? This will archive the current product and create a replacement.'
                    )
                  ) {
                    const dup = (await duplicate(id as string)) as {
                      data: IProduct;
                    };
                    toast.success('Product duplicated!', {
                      autoClose: 2000,
                    });
                    navigate(
                      Routes.PRODUCT.createLink?.(dup.data._id.toString()) ?? ''
                    );
                  }
                  break;
                case 'confirm':
                  if (
                    window.confirm(
                      'Are you sure you want to confirm this product? Confirmed products cannot be edited and must instead be replaced with a new product.'
                    )
                  ) {
                    setFieldValue('isConfirmed', true);
                    submitForm();
                  }
                  break;
                case 'archive':
                  if (
                    window.confirm(
                      'Are you sure you want to archive this product? Archived products cannot be restored.'
                    )
                  ) {
                    setFieldValue('isArchived', true);
                    submitForm();
                  }
                  break;
              }
            }}
            furtherActions={furtherActions}
          />

          {data?.addOnFor && (
            <p>
              This is an add on product.{' '}
              <Link to={`/products/${data.addOnFor}`}>Go to parent</Link>
            </p>
          )}
          <form onSubmit={handleSubmit}>
            <Card className={styles.settingsCard}>
              <CardContent>
                <FormLayout className={styles.productForm}>
                  <FormGroup
                    style={{
                      gridArea: 'scope',
                    }}
                  >
                    <Label htmlFor="scope">Scope</Label>
                    <Select
                      {...getFieldProps('scope')}
                      id="scope"
                      disabled={data?.isConfirmed || data?.isArchived}
                    >
                      <option value={ProductScope.Image}>Image</option>
                      <option value={ProductScope.Event}>Event</option>
                    </Select>
                  </FormGroup>
                  <FormGroup
                    style={{
                      gridArea: 'name',
                    }}
                  >
                    <Label htmlFor="name">Name</Label>
                    <Input
                      {...getFieldProps('name')}
                      disabled={data?.isConfirmed || data?.isArchived}
                      type="text"
                      id="name"
                    />
                  </FormGroup>
                  <FormGroup
                    style={{
                      gridArea: 'description',
                    }}
                  >
                    <Label htmlFor="description">Description</Label>
                    <TextArea
                      {...getFieldProps('description')}
                      cols={30}
                      rows={10}
                      id="description"
                      disabled={data?.isConfirmed || data?.isArchived}
                    />
                  </FormGroup>
                  <FormGroup
                    style={{
                      gridArea: 'isPublic',
                    }}
                  >
                    <Checkbox
                      label="Is Public?"
                      type="checkbox"
                      id="isPublic"
                      defaultChecked={data?.isPublic}
                      {...getFieldProps('isPublic')}
                      disabled={data?.isArchived}
                    />
                  </FormGroup>
                  <FormGroup
                    style={{
                      gridArea: 'pricingmodel',
                    }}
                  >
                    <Label htmlFor="pricingModel">Pricing model</Label>
                    <Select
                      disabled={
                        data?.isConfirmed ||
                        data?.addOnFor != null ||
                        data?.isArchived
                      }
                      {...getFieldProps('pricingModel')}
                      id="pricingModel"
                    >
                      <option value={PricingModel.Standard}>Standard</option>
                      <option value={PricingModel.Tiered}>Tiered</option>
                    </Select>
                  </FormGroup>
                  {values.pricingModel === PricingModel.Standard && (
                    <FormGroup
                      style={{
                        gridArea: 'price',
                      }}
                    >
                      <Label htmlFor="price">Price</Label>
                      <Input
                        {...getFieldProps('price')}
                        type="number"
                        id="price"
                        disabled={data?.isConfirmed || data?.isArchived}
                      />
                    </FormGroup>
                  )}
                  {values.pricingModel === PricingModel.Tiered && (
                    <FieldArray
                      name="priceTiers"
                      render={({ push, remove }) => (
                        <FormGroup
                          style={{
                            gridArea: 'price',
                          }}
                        >
                          <Label htmlFor="price">Price tiers</Label>
                          <ul className={styles.priceTiers}>
                            {values.priceTiers?.map((tier, index) => (
                              <li key={index} className={styles.priceTier}>
                                <FormGroup>
                                  {index === 0 && (
                                    <Label htmlFor="price">Min</Label>
                                  )}
                                  <Input
                                    disabled={true}
                                    {...getFieldProps(
                                      `priceTiers.${index}.minVolume`
                                    )}
                                    type="number"
                                  />
                                </FormGroup>
                                <FormGroup>
                                  {index === 0 && (
                                    <Label htmlFor="price">Max</Label>
                                  )}

                                  {tier.maxVolume === undefined ||
                                  !tier.maxVolume ? (
                                    <Input
                                      disabled={true}
                                      type="text"
                                      value="∞"
                                    />
                                  ) : (
                                    <Input
                                      {...getFieldProps(
                                        `priceTiers.${index}.maxVolume`
                                      )}
                                      onChange={(evt) => {
                                        setFieldValue(
                                          `priceTiers.${index}.maxVolume`,
                                          +evt.target.value
                                        );
                                        setFieldValue(
                                          `priceTiers.${index + 1}.minVolume`,
                                          +evt.target.value + 1
                                        );
                                      }}
                                      type="number"
                                      disabled={
                                        data?.isConfirmed || data?.isArchived
                                      }
                                    />
                                  )}
                                </FormGroup>
                                <FormGroup>
                                  {index === 0 && (
                                    <Label htmlFor="price">Price</Label>
                                  )}
                                  <Input
                                    {...getFieldProps(
                                      `priceTiers.${index}.price`
                                    )}
                                    type="number"
                                    disabled={
                                      data?.isConfirmed || data?.isArchived
                                    }
                                  />
                                </FormGroup>
                                <XMarkIcon
                                  onClick={() => {
                                    if (data?.isConfirmed || data?.isArchived) {
                                      return;
                                    }
                                    if (index > 0) {
                                      if (values.priceTiers?.[index + 1]) {
                                        setFieldValue(
                                          `priceTiers.${index + 1}.minVolume`,
                                          (values.priceTiers?.[index - 1]
                                            .maxVolume ?? 1) + 1
                                        );
                                      }
                                    }
                                    remove(index);
                                    if (index === 0) {
                                      setFieldValue(
                                        `priceTiers.${0}.minVolume`,
                                        1
                                      );
                                    }
                                    if (
                                      index ===
                                      (values.priceTiers?.length ?? 0) - 1
                                    ) {
                                      setFieldValue(
                                        `priceTiers.${
                                          (values.priceTiers?.length ?? 0) - 2
                                        }.maxVolume`,
                                        undefined
                                      );
                                    }
                                  }}
                                  className={styles.tierRemover}
                                />
                              </li>
                            ))}
                          </ul>
                          <Button
                            disabled={data?.isConfirmed || data?.isArchived}
                            onClick={() => {
                              const newIndex = values.priceTiers?.length ?? 0;
                              if (newIndex > 0) {
                                //set previous to min + 1
                                const newValue =
                                  (values.priceTiers?.[newIndex - 1]
                                    .minVolume ?? 1) + 1;
                                setFieldValue(
                                  `priceTiers.${newIndex - 1}.maxVolume`,
                                  newValue
                                );
                                push({
                                  minVolume: newValue + 1,
                                  maxVolume: undefined,
                                  price: 0,
                                });
                              } else {
                                push({
                                  minVolume: 1,
                                  maxVolume: undefined,
                                  price: 0,
                                });
                              }
                            }}
                          >
                            Add tier
                          </Button>
                        </FormGroup>
                      )}
                    />
                  )}
                  <FormGroup
                    style={{
                      gridArea: 'shippingcost',
                    }}
                  >
                    <Label htmlFor="shippingCost">Shipping cost</Label>
                    <Input
                      {...getFieldProps('shippingCost')}
                      type="number"
                      id="shippingCost"
                      disabled={data?.isConfirmed || data?.isArchived}
                    />
                  </FormGroup>
                  <FormGroup
                    style={{
                      gridArea: 'deliverymechanism',
                    }}
                  >
                    <Label htmlFor="deliveryMechanism">
                      Delivery mechanism
                    </Label>
                    <Select
                      {...getFieldProps('deliveryMechanism')}
                      id="deliveryMechanism"
                      disabled={data?.isConfirmed || data?.isArchived}
                    >
                      <option value={DeliveryMechanism.Email}>Email</option>
                      <option value={DeliveryMechanism.Post}>Post</option>
                    </Select>
                  </FormGroup>
                  <FormGroup
                    style={{
                      gridArea: 'imagesrequired',
                    }}
                  >
                    <Label htmlFor="imagesRequired">Images required</Label>
                    <Input
                      {...getFieldProps('imagesRequired')}
                      type="number"
                      id="imagesRequired"
                      disabled={data?.isConfirmed || data?.isArchived}
                    />
                  </FormGroup>
                  <FormGroup
                    style={{
                      gridArea: 'instantdownload',
                    }}
                  >
                    <Checkbox
                      label="Instant download"
                      type="checkbox"
                      id="instantDownload"
                      defaultChecked={data?.isInstantDownload}
                      {...getFieldProps('isInstantDownload')}
                      disabled={data?.isConfirmed || data?.isArchived}
                    />
                  </FormGroup>

                  {values.isInstantDownload && (
                    <FormGroup
                      style={{
                        gridArea: 'longsidelength',
                      }}
                    >
                      <Label htmlFor="longSideLength">
                        Long side length (px)
                      </Label>
                      <Input
                        {...getFieldProps('lengthLongSide')}
                        type="number"
                        id="longSideLength"
                        disabled={data?.isConfirmed || data?.isArchived}
                      />
                    </FormGroup>
                  )}
                </FormLayout>
              </CardContent>
            </Card>

            <Card className={styles.productFormCard}>
              <CardContent>
                <Label htmlFor="">Product Questions</Label>
                <FormBuilder
                  fields={values.formSchema || []}
                  onChange={(fields) => setFieldValue('formSchema', fields)}
                  disabled={data?.isConfirmed || data?.isArchived}
                />
              </CardContent>
            </Card>

            {data?.scope === ProductScope.Event && <ProductPageContent />}

            {data?.addOnFor == null && (
              <Card className={styles.addOnTable}>
                <FormGroup>
                  <CardContent>
                    <Label htmlFor="">Add ons</Label>
                  </CardContent>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Price</th>
                        <th>Is Confirmed?</th>
                        <th>Is Archived?</th>
                        <th>Is Public?</th>
                        {!data?.isArchived && <th>&nbsp;</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {addOnsLoading && (
                        <tr>
                          <td colSpan={4}>Loading...</td>
                        </tr>
                      )}
                      {addOnsData?.map((ao) => (
                        <tr key={ao._id.toString()}>
                          <td>{ao.name}</td>
                          <td>{ao.description}</td>
                          <td>
                            <Currency value={ao.price} />
                          </td>
                          <td>{ao.isConfirmed ? 'Yes' : 'No'}</td>
                          <td>{ao.isArchived ? 'Yes' : 'No'}</td>
                          <td>{ao.isPublic ? 'Yes' : 'No'}</td>
                          {!data?.isArchived && (
                            <td>
                              <Link to={`/products/${ao._id}`}>Edit</Link>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </FormGroup>
              </Card>
            )}
          </form>
        </>
      )}
    </Formik>
  );
};
